import { InputHTMLAttributes, useRef } from 'react';

import classnames from 'classnames';

import { BlobToDataURL } from '@/helpers/parsers';
import { useTranslation } from '@/i18n';

import FileImagePreview from '../../global/FileImagePreview';

import classes from './index.module.scss';

type FilePreviewType = string | File;

export type InlineImageUploadProps = {
  className?: string;
  hiddenProps?: Omit<InputHTMLAttributes<HTMLInputElement>, 'value'> & { value?: any };
  id?: string;
  onUpload: (files: File, results: string) => void;
  preview?: FilePreviewType;
  readonly?: boolean;
  onClick?: () => void;
};

export const InlineImageUpload: React.FC<InlineImageUploadProps> = ({
  className,
  hiddenProps,
  id,
  onUpload,
  preview,
  readonly = false,
  onClick,
  ...props
}) => {
  const { t } = useTranslation('common');
  const input = useRef<HTMLInputElement>(null);

  const handleSelectFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    await handleUpload(files);

    e.target.value = '';
  };

  const handleUpload = async (files: File[]) => {
    if (!files || !files.length) return;

    try {
      const results = await Promise.all(files.map((file) => BlobToDataURL(file)));
      if (files && files.length) {
        onUpload?.(files[0], results[0]);
        hiddenProps?.onChange?.(files[0] as any);
      }
    } catch (e) {}
  };

  if (readonly && preview) {
    return (
      <div className={classes.inlineContainer}>
        <div className={classes.overlay} onClick={() => onClick && onClick()}>
          {t('clickToEdit')}
        </div>
        <div className={classnames(classes.preview, classes.rounded)}>
          <FileImagePreview file={preview} size={150} fit />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.inlineContainer}>
      <input
        accept={['image/jpeg', 'image/png'].join(',')}
        className={classes.fileInput}
        id={id}
        onChange={handleSelectFiles}
        ref={input}
        type="file"
      />
      <div className={classes.overlay} onClick={() => input.current?.click()}>
        {t('clickToEdit')}
      </div>
      <input {...hiddenProps} type="hidden" />
      {preview && (
        <div className={classnames(classes.preview, classes.rounded)}>
          <FileImagePreview file={preview} size={150} fit />
        </div>
      )}
    </div>
  );
};
